import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useCurrentUser } from '../../../Session';
import { CreatePurchaseTransactionMutationVariables, useCreatePurchaseTransactionMutation } from '../../../api';
import { useAlert } from '../../../components/AlertProvider';
import { useFlutter } from '../../../components/FlutterProvider';
import { useHandleError } from '../../../utilities/handleErrors';
import { DeliveryPeriodType } from '../../../utilities/useDeliveryPeriodOptions';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';
import {
    CreatePurchaseTransactionHeader,
    CreatePurchaseTransactionBody,
    CreatePurchaseTransactionFooter,
    CheckDuplicateVehicle,
} from './components';

export type CreatePurchaseTransactionFormValues = {
    handover: CreatePurchaseTransactionMutationVariables['fields']['handover'] & {
        targetHandoverDate?: Date;
        targetHandoverTime?: Date;
        handoverLocationField: { main: string; other?: string };
    };
} & CreatePurchaseTransactionMutationVariables['fields'];

const preparePayload = (
    values: CreatePurchaseTransactionFormValues
): CreatePurchaseTransactionMutationVariables['fields'] => {
    // we need to override the handover object so extract it
    const { handover, ...fields } = values;

    const { handoverLocationField, handoverLocation, ...handoverFields } = handover;

    return {
        handover: {
            ...handoverFields,
            handoverLocation: handoverLocationField.other || handoverLocationField.main,
            targetHandoverDateTime: null, // always null on creation
        },
        ...fields,
    };
};

const formValidator = validators.compose(
    validators.requiredString('vehicle.number'),
    validators.requiredNumber('vehicle.mileage'),
    validators.requiredString('customer.ownerIdType'),
    validators.requiredString('customer.ownerId'),
    validators.requiredDate('vehicle.intendedDeregistrationDate'),
    validators.requiredString('vehicle.make'),
    validators.requiredString('vehicle.model'),
    validators.requiredString('vehicle.primaryColour'),
    validators.requiredNumber('vehicle.manufacturingYear'),
    validators.requiredString('vehicle.chassisNumber'),
    validators.requiredString('vehicle.maximumPowerOutput'),
    validators.requiredNumber('vehicle.openMarketValue'),
    validators.requiredDate('vehicle.originalRegistrationDate'),
    validators.requiredDate('vehicle.firstRegistrationDate'),
    validators.requiredNumber('vehicle.transferCount'),
    validators.requiredNumber('vehicle.actualARFPaid'),
    validators.requiredDate('vehicle.coeExpiryDate'),
    validators.requiredString('vehicle.coeCategory'),
    validators.requiredNumber('vehicle.coePeriodYear'),
    validators.requiredString('handover.personResponsible'),
    validators.requiredString('vehicle.importMethod'),

    validators.only(
        ({ vehicle: { parfEligibility } }) => parfEligibility === true,
        validators.requiredDate('vehicle.parfEligibilityDate')
    ),

    validators.only(
        ({ handover: { deliveryPeriod } }) => deliveryPeriod === DeliveryPeriodType.IndentOrder,
        validators.requiredString('handover.deliveryPeriodIndentOrder')
    )
);

export type CreatePurchaseTransactionProps = {
    initialValues: CreatePurchaseTransactionFormValues;
    checkDuplicateVehicle: CheckDuplicateVehicle;
};

const CreatePurchaseTransaction = ({ initialValues, checkDuplicateVehicle }: CreatePurchaseTransactionProps) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const [createPurchaseTransaction] = useCreatePurchaseTransactionMutation();
    const history = useHistory();
    const { capabilities } = useFlutter();
    const { hasOneMonitoring } = capabilities;
    const currentUser = useCurrentUser();
    const { t } = useTranslation(['purchaseTransactionsPage']);
    const { show } = useAlert();

    const [showForm, setShowForm] = useState(!hasOneMonitoring || currentUser.testAccount);

    const onSubmit = useHandleError(
        async (values: CreatePurchaseTransactionFormValues) => {
            if (!values.vehicle.isOneMotoringData) {
                const isDuplicated = await checkDuplicateVehicle(values.vehicle);
                if (isDuplicated) {
                    // do nothing;
                    return;
                }
            }

            // prepare payload for the mutation
            const fields = preparePayload(values);
            // execute the mutation
            await createPurchaseTransaction({ variables: { fields } });

            show('success', t('purchaseTransactionsPage:successfulMessage.transactionCreated'));

            // then go back from where we came
            history.goBack();
        },
        [createPurchaseTransaction, history]
    );

    const validate = useValidator(formValidator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {() => (
                <Form>
                    <CreatePurchaseTransactionHeader showForm={showForm} />
                    <CreatePurchaseTransactionBody
                        checkDuplicateVehicle={checkDuplicateVehicle}
                        setShowForm={setShowForm}
                        showForm={showForm}
                    />
                    {isSmall && <CreatePurchaseTransactionFooter showForm={showForm} />}
                </Form>
            )}
        </Formik>
    );
};

export default CreatePurchaseTransaction;
